import React, { useRef, useEffect, useCallback, useState } from 'react';

const ScratchCard = ({
  overlayImage,
  children,
  onReveal,
  width,
  height = 160,
  brushSize = 30,
  minScratchPercent = 35,
  className = '',
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const hasCalledRevealRef = useRef(false);
  const isDrawingRef = useRef(false);
  const canvasLoadedRef = useRef(false);
  const scratchedAreaRef = useRef(0);
  const [cardWidth, setCardWidth] = useState(width ?? 300);
  const totalArea = cardWidth * height;
  const circleArea = Math.PI * brushSize * brushSize;

  const checkReveal = useCallback((usePixelCount = false) => {
    let shouldReveal = false;
    if (usePixelCount && canvasRef.current) {
      try {
        const ctx = canvasRef.current.getContext('2d');
        const w = canvasRef.current.width;
        const h = canvasRef.current.height;
        const imageData = ctx.getImageData(0, 0, w, h);
        const pixels = imageData.data;
        let transparentCount = 0;
        const total = w * h;
        for (let i = 3; i < pixels.length; i += 4) {
          if (pixels[i] < 128) transparentCount++;
        }
        shouldReveal = (transparentCount / total) * 100 >= minScratchPercent;
      } catch (e) {
        shouldReveal = scratchedAreaRef.current >= totalArea * (minScratchPercent / 100);
      }
    } else {
      shouldReveal = scratchedAreaRef.current >= totalArea * (minScratchPercent / 100);
    }
    if (shouldReveal && !hasCalledRevealRef.current) {
      hasCalledRevealRef.current = true;
      onReveal?.();
    }
  }, [minScratchPercent, onReveal]);

  const getCoords = useCallback((e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }, []);

  const scratch = useCallback((e) => {
    e.preventDefault();
    if (!canvasRef.current || !canvasLoadedRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, brushSize, 0, Math.PI * 2);
    ctx.fill();
    scratchedAreaRef.current += circleArea;
    checkReveal(false);
  }, [brushSize, getCoords, checkReveal, circleArea]);

  const handlePointerDown = (e) => {
    isDrawingRef.current = true;
    scratch(e);
  };

  const handlePointerMove = (e) => {
    if (isDrawingRef.current) scratch(e);
  };

  const handlePointerUp = () => {
    isDrawingRef.current = false;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || width) return;
    const updateWidth = () => {
      const w = container.offsetWidth;
      if (w > 0) setCardWidth(w);
    };
    const ro = new ResizeObserver(() => updateWidth());
    ro.observe(container);
    updateWidth();
    return () => ro.disconnect();
  }, [width]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !overlayImage) return;

    scratchedAreaRef.current = 0;
    const actualWidth = width ?? Math.max(cardWidth, 200);
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      ctx.drawImage(img, 0, 0, actualWidth, height);
      canvasLoadedRef.current = true;
    };
    img.onerror = () => {
      ctx.fillStyle = '#8B7355';
      ctx.fillRect(0, 0, actualWidth, height);
      canvasLoadedRef.current = true;
    };
    img.src = overlayImage;
  }, [overlayImage, width, cardWidth, height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const handleTouchMove = (e) => e.preventDefault();
    canvas?.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => canvas?.removeEventListener('touchmove', handleTouchMove);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`valentines-scratch-card ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        height,
        overflow: 'hidden',
        touchAction: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        {children}
      </div>
      <canvas
          ref={canvasRef}
          width={width ?? Math.max(cardWidth, 200)}
          height={height}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            cursor: 'crosshair',
            touchAction: 'none',
            pointerEvents: 'auto',
            zIndex: 1000,
          }}
        />
    </div>
  );
};

export default ScratchCard;
